import type { ProductCategoryType } from '@/types/domains/index';

type ProductContentConstantsType = {
  CATEGORY_JAPANESE_NAME: Record<string, ProductCategoryType.Category>;
  CATEGORY_ENGLISH_NAME: Record<string, ProductCategoryType.CategoryEnglishName>;
  ENGLISH_NAME_TO_ID: Record<ProductCategoryType.CategoryEnglishName, number>;
};

/**
 * 商品コンテンツのカテゴリ。ID 4「食品」はサプリメントから5コードを分離して新設した。
 *
 * **ID 3 の slug は `health-goods` → `personal-care` に改名済み**（決定#24。#1333 で実施）。
 * 表示名（パーソナルケア）に slug を合わせたもので、**旧 `/health-goods` は
 * `/?category=personal-care#products` へ 301**（`nuxt.config.ts` の `routeRules`）。
 * **ID 1 の slug も `functional-food` → `supplement` に改名済み**（表示名「サプリメント」に合わせた。
 * 旧 `/functional-food` は `/?category=supplement#products` へ 301）。
 * **この値はURLのクエリキーで、APIには送らない** — `useProductContent.ts` は
 * `/productContents/all` `/productContents/top` の固定URLを叩き、`ENGLISH_NAME_TO_ID` は
 * クライアント側のフィルタキーにすぎない（型が `types/externals/obbsApi/` にあるので紛らわしい）。
 */
export const PRODUCT_CONTENT_CONSTANTS: ProductContentConstantsType = {
  CATEGORY_JAPANESE_NAME: {
    1: 'サプリメント',
    2: 'スキンケア',
    3: 'パーソナルケア',
    4: '食品',
  },
  CATEGORY_ENGLISH_NAME: {
    1: 'supplement',
    2: 'skin-care',
    3: 'personal-care',
    4: 'food',
  },
  ENGLISH_NAME_TO_ID: {
    supplement: 1,
    'skin-care': 2,
    'personal-care': 3,
    food: 4,
  },
} as const;

/**
 * カテゴリの表示順（サプリメント → 食品 → スキンケア → パーソナルケア）。
 * 支給デザインのトップの商品タブ・フッターナビ・SPアコーディオンがすべてこの順。
 * ID の昇順ではないので、`Object.entries` の列挙順に頼らずこの配列を使う。
 */
export const CATEGORY_DISPLAY_ORDER: ReadonlyArray<number> = [1, 4, 2, 3] as const;

/**
 * 商品コード → カテゴリID の対応表（38コード）。**カテゴリの振り分けはこの表を正とする。**
 *
 * obbs_api はカテゴリを受け取らず、絞り込みはフロント側で行う（`useProductContent`）。
 * API が返す `productContentsCategoryId` は旧3分類のままなので、
 * この表で上書きしてから絞り込む。表に無い商品コードは API の値をそのまま使うので、
 * 新商品が登録されても表示から漏れることはない。
 *
 * 振り分けは支給デザインの商品画像URL（`/image/product/<コード>/`）から抽出した。
 * `K42` と `KF02` は支給デザインに未掲載だが実データにあるので含める。
 */
export const PRODUCT_CODE_TO_CATEGORY_ID: Readonly<Record<string, number>> = {
  // サプリメント（17）
  '210': 1,
  '501': 1,
  '510': 1,
  '512': 1,
  '513': 1,
  '516': 1,
  '520': 1,
  '525': 1,
  '530': 1,
  '549': 1,
  '558': 1,
  '559': 1,
  '592': 1,
  '777': 1,
  '810': 1,
  L61: 1,
  L62: 1,
  // スキンケア（4）
  NS1: 2,
  NS2: 2,
  NS3: 2,
  NST: 2,
  // パーソナルケア（12）
  K41: 3,
  K42: 3,
  K43: 3,
  K44: 3,
  K45: 3,
  KF01: 3,
  KF02: 3,
  KF03: 3,
  KF05: 3,
  KF15: 3,
  KG01: 3,
  KG02: 3,
  // 食品（5）
  '710': 4,
  QBD01: 4,
  QBD02: 4,
  QBM01: 4,
  QBM02: 4,
} as const;
